import { useEffect, useRef } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { WalletReadyState } from '@solana/wallet-adapter-base'
import { useAuth } from '../auth/AuthContext'

/**
 * Bridges the app's auth flow (which connects via the wallet extension directly
 * for JWT nonce-signing) with the `@solana/wallet-adapter-react` provider used
 * by Trade-page transaction signing. Without this hook the adapter is "in the
 * tree but not connected", so `useWallet().connected` stays false and every
 * Trade-form button shows "Connect wallet".
 *
 * The bridge:
 *  1. Selects the adapter that matches `walletType` from AuthContext — once per
 *     walletType value. A ref prevents the feedback loop where WalletProvider
 *     unsets the adapter on a failed connect, which would re-trigger selection,
 *     which would re-trigger autoConnect, endlessly.
 *  2. Calls `connect()` once the wallet is selected and ready.
 *
 * Because Phantom/Solflare have already approved the origin during the auth
 * handshake, this is typically a silent no-popup connect.
 */
export function useWalletAdapterBridge() {
  const { walletType } = useAuth()
  const { wallets, wallet, select, connect, connecting, connected } = useWallet()

  // Track which walletType we last attempted a select for.
  // WalletProvider calls changeWallet(null) when adapter.connect() fails —
  // without this guard that would flip wallet→null, re-trigger this effect,
  // re-select, and loop forever.
  const selectedForRef = useRef<string | null>(null)

  // Step 1: select the right adapter by name — once per walletType
  useEffect(() => {
    if (!walletType) {
      selectedForRef.current = null  // reset on logout so next login works
      return
    }
    const desired = walletType === 'phantom' ? 'Phantom'
                   : walletType === 'solflare' ? 'Solflare'
                   : null
    if (!desired) return
    if (wallet?.adapter.name === desired) return  // already correct
    if (selectedForRef.current === walletType) return  // already attempted — don't loop
    const match = wallets.find(w => w.adapter.name === desired)
    if (match) {
      selectedForRef.current = walletType
      select(match.adapter.name)
    }
  }, [walletType, wallet, wallets, select])

  // Step 2: connect once selected (fallback if WalletProvider autoConnect skipped)
  useEffect(() => {
    if (!wallet || connected || connecting) return
    if (wallet.readyState !== WalletReadyState.Installed && wallet.readyState !== WalletReadyState.Loadable) return
    connect().catch(err => {
      // The adapter throws if the wallet is locked or the user dismissed the
      // popup. User can retry by clicking the "Connect & [action]" button.
      console.warn('[walletBridge] connect failed', err?.message ?? err)
    })
  }, [wallet, connected, connecting, connect])
}
